import { FC, useEffect, useMemo } from 'react'
import { GRAY_800, MOBILE_VIEW_WIDTH_QUERY } from 'constants/styling/theme'
import { Navigate, Outlet } from 'react-router-dom'
import { WorkspaceTab, useWorkspaceNavigation } from './contexts'

import Box from '@mui/material/Box'
import DesktopWindowsOutlined from '@mui/icons-material/DesktopWindowsOutlined'
import { EmptyContent } from 'components/common/EmptyContent'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { GalleryHeader } from 'components/common/Gallery/GalleryHeader'
import { PageHeader } from 'components/common/Page/PageHeader'
import { PageLayout } from 'components/common/Page/PageLayout'
import { PageTab } from 'components/common/Page/PageTab'
import { PageTabs } from 'components/common/Page/PageTabs'
import { PageTransition } from 'utils/animations'
import { Path } from 'constants/router'
import { PropstackPurposeBanner } from 'components/common/PropstackPurposeBanner'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTranslation } from 'react-i18next'
import { logger } from 'utils/logging'
import { useUserData } from 'components/contexts/UserDataContext'
import { useWorkspaceRoles } from 'utils/hooks'

export const WorkspacePageController: FC = () => {
  const { t } = useTranslation(['workspace_settings'])
  const { activeTab, handleChangeTab } = useWorkspaceNavigation()
  const { currentUserWorkspace } = useUserData()
  const { isWorkspaceAdmin, isWorkspaceSponsored, isPropstackLinked, isRestrictedMember } = useWorkspaceRoles()
  const isMobileView = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)

  const propstackIntegration = useFeatureFlag(FeatureFlag.PROPSTACK_INTEGRATION)
  // Members are hidden for restricted members (ISSUE-2488) — they must not see the other members.
  // A Restricted Admin still sees them (isRestrictedMember is false for admins) so they can invite.
  const canSeeMembers = useMemo(() => !!currentUserWorkspace && !isWorkspaceSponsored && !isRestrictedMember, [currentUserWorkspace, isWorkspaceSponsored, isRestrictedMember])
  const canSeeIntegrations = isWorkspaceAdmin && isPropstackLinked && propstackIntegration

  // A restricted member has no accessible tab left once Members is hidden, so the whole settings page
  // is closed to them (mirrors the sponsored-member redirect). Three gates collapse onto one redirect,
  // so surface which one fired (ISSUE-2488) — otherwise a bounced user is undebuggable in production.
  const accessDeniedReason = useMemo<string | null>(() => {
    if (!currentUserWorkspace) return 'no_workspace'
    if (isWorkspaceSponsored) return 'sponsored'
    if (isRestrictedMember) return 'restricted_member'
    return null
  }, [currentUserWorkspace, isWorkspaceSponsored, isRestrictedMember])

  // Only the seat-type entitlement denials are worth a breadcrumb. The no-workspace case
  // fires on benign stale bookmarks and cold loads, so logging it would be pure noise. Info
  // level stays out of Sentry by design (the logger only forwards warn/error), so this is a local
  // debugging breadcrumb, not a Sentry event.
  useEffect(() => {
    if (accessDeniedReason === 'sponsored' || accessDeniedReason === 'restricted_member') {
      logger.info('Workspace settings access denied by seat type', undefined, { context: { component: 'WorkspacePageController', action: 'accessDenied', metadata: { reason: accessDeniedReason } } })
    }
  }, [accessDeniedReason])

  if (accessDeniedReason) return <Navigate to={Path.INDEX} replace state={{ workspaceAccessDenied: true }} />

  // Hidden tabs are inaccessible to everyone (including admins) — direct URL access redirects to the dashboard
  const hiddenTabs: WorkspaceTab[] = [
    WorkspaceTab.BRAND_ASSETS,
    WorkspaceTab.BILLING,
    ...(canSeeIntegrations ? [] : [WorkspaceTab.INTEGRATIONS]),
  ]
  if (hiddenTabs.includes(activeTab)) {
    return <Navigate to={Path.INDEX} replace />
  }

  // Members landing on admin-only tabs get redirected to the dashboard
  const adminOnlyTabs: WorkspaceTab[] = [WorkspaceTab.WORKSPACE, WorkspaceTab.PLAN, WorkspaceTab.INTEGRATIONS]
  if (!isWorkspaceAdmin && adminOnlyTabs.includes(activeTab)) {
    return <Navigate to={Path.INDEX} replace />
  }

  return (
    <PageTransition>
      <PageLayout
        layoutErrorContent={null}
        header={
          <PageHeader>
            <GalleryHeader title={t('workspace_settings:title')} />
          </PageHeader>
        }
        tabs={
          <>

            {!isMobileView && <PropstackPurposeBanner sx={{ marginBottom: '2.4rem' }} />}

            {!isMobileView &&
              <PageTabs>
                {isWorkspaceAdmin && <PageTab tab={WorkspaceTab.WORKSPACE} activeTab={activeTab} tabText={t('workspace_settings:tabs.workspace')} onClick={() => handleChangeTab(WorkspaceTab.WORKSPACE)} />}
                {isWorkspaceAdmin && <PageTab tab={WorkspaceTab.PLAN} activeTab={activeTab} tabText={t('workspace_settings:tabs.plan')} onClick={() => handleChangeTab(WorkspaceTab.PLAN)} />}
                {canSeeIntegrations && <PageTab tab={WorkspaceTab.INTEGRATIONS} activeTab={activeTab} tabText={t('workspace_settings:tabs.integrations')} onClick={() => handleChangeTab(WorkspaceTab.INTEGRATIONS)} />}
                {canSeeMembers && <PageTab tab={WorkspaceTab.MEMBERS} activeTab={activeTab} tabText={t('workspace_settings:tabs.members')} onClick={() => handleChangeTab(WorkspaceTab.MEMBERS)} />}
              </PageTabs>
            }

          </>
        }
      >
        {isMobileView ? (
          <Box maxWidth="60rem" width="100%" margin="auto" marginTop="8rem" padding="0 2rem">
            <EmptyContent title={t('workspace_settings:unsupported_device.title')} description={t('workspace_settings:unsupported_device.description')} icon={<DesktopWindowsOutlined sx={{ color: GRAY_800, fontSize: '2.8rem' }} />} />
          </Box>
        ) : (
          <Outlet />
        )}
      </PageLayout>
    </PageTransition>
  )
}
